import * as React from 'react';
import {
    FloatingPeoplePicker,
    SuggestionsStore,
    IPeopleFloatingPickerProps,
} from '@fluentui/react/lib/FloatingPicker';
import getSuggestionProps from '../utils/getSuggestionProps';
import { ITheme } from '@uifabric/styling';
import LocationCommonWellItemViewState from '../store/schema/LocationCommonWellItemViewState';
import LocationPickerState, { SearchContext } from '../store/schema/LocationPickerState';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import LocationSuggestionItem from '../components/LocationSuggestionItem';
import getLocationSuggestions from './getLocationSuggestions';
import { deletePlace } from 'owa-calendar-scheduling-service/lib/services/deletePlaceService';
import { logUsage, PerformanceDatapoint } from 'owa-analytics';

let performanceDatapoint: PerformanceDatapoint = null;

/**
 * Location floating picker props
 */
export default function getLocationPickerProps(
    getPickerState: () => LocationPickerState,
    onPickerStateChanged: (state: Partial<LocationPickerState>) => void,
    getSearchContext: () => SearchContext,
    theme: ITheme,
    colorScheme: ColorScheme,
    roomFreeBusyEnabled: boolean,
    disableUseThisLocation: boolean,
    onUseThisLocationClicked: (locationString: string) => void,
    zeroQueryDisabled: boolean,
    showAddRoom: boolean,
    onAddRoomClicked: () => void,
    calloutWidth: number,
    findControl: FloatingPeoplePicker,
    canRemoveSuggestion: boolean,
    onTextInputChanged?: (text: string) => void
): IPeopleFloatingPickerProps {
    return {
        suggestionsStore: new SuggestionsStore<LocationCommonWellItemViewState>(),
        onRenderSuggestionsItem: onRenderSuggestion(roomFreeBusyEnabled, colorScheme, theme),
        onResolveSuggestions: getQuerySuggestions(
            getPickerState,
            getSearchContext,
            onPickerStateChanged
        ),
        onZeroQuerySuggestion: zeroQueryDisabled
            ? null
            : getZeroQuerySuggestions(getPickerState, getSearchContext, onPickerStateChanged),
        calloutWidth: calloutWidth ? calloutWidth : 380,
        resolveDelay: 150,
        pickerSuggestionsProps: getSuggestionProps(
            getPickerState().queryString,
            getPickerState().findLocationRequestStatus,
            getPickerState().suggestionSet.length,
            theme,
            colorScheme,
            disableUseThisLocation,
            onUseThisLocationClicked,
            showAddRoom,
            onAddRoomClicked,
            canRemoveSuggestion
        ),
        onInputChanged: onTextInputChanged,
        onRemoveSuggestion: onRemoveSuggestion(getPickerState(), findControl, onPickerStateChanged),
    };
}

function onRemoveSuggestion(
    pickerState: LocationPickerState,
    findControl: FloatingPeoplePicker,
    onPickerStateChanged: (state: Partial<LocationPickerState>) => void
): (suggestionToRemove: LocationCommonWellItemViewState) => void {
    return function (suggestionToRemove: LocationCommonWellItemViewState) {
        deletePlace(suggestionToRemove.persona.personaId);

        const suggestionSet = pickerState.suggestionSet.filter((suggestion, index) => {
            if (suggestion.persona.personaId === suggestionToRemove.persona.personaId) {
                logSuggestionRemovalUsage(suggestionToRemove, index);
                return false;
            }
            return true;
        });

        onPickerStateChanged({ suggestionSet: suggestionSet });

        findControl.updateSuggestions(suggestionSet, true /*updateUI*/);
    };
}

function logSuggestionRemovalUsage(
    suggestionRemoved: LocationCommonWellItemViewState,
    index: number
) {
    const persona = suggestionRemoved.persona;

    logUsage('LocationSuggestionRemovalUsage', {
        entityType: persona.entityType,
        queryLength: persona.relevanceData.queryString.length,
        index: index,
    });
}

function onRenderSuggestion(
    roomFreeBusyEnabled: boolean,
    colorScheme: ColorScheme,
    theme: ITheme
): (props: LocationCommonWellItemViewState, itemProps: any) => JSX.Element {
    return function (props: LocationCommonWellItemViewState, itemProps: any): JSX.Element {
        const { persona } = props;

        return (
            <LocationSuggestionItem
                key={persona.personaControlId}
                locationResult={persona}
                colorScheme={colorScheme}
                roomFreeBusyEnabled={roomFreeBusyEnabled}
                itemProps={itemProps}
                theme={theme}
            />
        );
    };
}

function getQuerySuggestions(
    getPickerState: () => LocationPickerState,
    getSearchContext: () => SearchContext,
    onPickerStateChanged: (state: Partial<LocationPickerState>) => void
): (
    queryString: string,
    currentItems: LocationCommonWellItemViewState[]
) => Promise<LocationCommonWellItemViewState[]> {
    return async function (queryString: string, currentItems: LocationCommonWellItemViewState[]) {
        performanceDatapoint = new PerformanceDatapoint('LocationsSuggestions');
        performanceDatapoint.addCustomData({
            isInitial: false,
        });
        const locationSuggestions = await getLocationSuggestions(
            queryString,
            getPickerState,
            getSearchContext(),
            onPickerStateChanged
        );
        performanceDatapoint.end();
        return locationSuggestions;
    };
}

function getZeroQuerySuggestions(
    getPickerState: () => LocationPickerState,
    getSearchContext: () => SearchContext,
    onPickerStateChanged: (state: Partial<LocationPickerState>) => void
): (currentItems: LocationCommonWellItemViewState[]) => Promise<LocationCommonWellItemViewState[]> {
    return async function (currentItems: LocationCommonWellItemViewState[]) {
        performanceDatapoint = new PerformanceDatapoint('LocationsSuggestions');
        performanceDatapoint.addCustomData({
            isInitial: true,
        });
        const locationSuggestions = await getLocationSuggestions(
            '' /* queryString */,
            getPickerState,
            getSearchContext(),
            onPickerStateChanged
        );
        performanceDatapoint.end();
        return locationSuggestions;
    };
}
